import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import styles from './purchase.module.css';
import { Icon } from 'assets/icons';
import { SubscriptionPurchaseContext } from 'contexts/checkout/subscription';
import { useModuleService } from 'hooks/sales/module';
import { SubscriptionPurchaseItem } from './item';
import { useCurrencyService } from 'hooks/sales/currency';
import { MultiSelect } from 'app/shared/select';
import { toReadableNumber } from 'app/shared/utils/number';
import { Button } from 'app/shared/button';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { useTaxService } from 'hooks/sales/tax';
import { usePaymentService } from 'hooks/sales/payment';
import { useContactService } from 'hooks/users/contact';
import { useTenant } from 'contexts/tenant';
import { prepareCheckoutConfig } from 'app/member/modules/wallet/helper';
import { toast } from 'react-toastify';
import { Spacer } from 'app/layouts/generic';
import Spinner from 'app/shared/spinners/spinner-50/spinner-50';
import GmModal from 'app/shared/modal/modal';
import { ContactSales } from '../contact';
import { VirtualAccountContext } from 'contexts/checkout/virtual';
import useScreenSize from 'hooks/size';
import { PricingComparison } from './comparison';

export const SubscriptionPurchaseModal = () => {
  const { tenant } = useTenant();
  const { closeModal, module_code, setModuleCode } = useContext(SubscriptionPurchaseContext);
  const { openModal, setPaymentDetails: setVirtualPaymentDetails } =
    useContext(VirtualAccountContext);
  const { isMobile } = useScreenSize();

  const { fetchContacts } = useContactService();
  const { fetchCurrencies, getCurrencySymbol } = useCurrencyService();
  const { fetchModules } = useModuleService();
  const { createPayment } = usePaymentService();
  const { buyFreemiumSubscription, fetchSubscription } = useSubscriptionService();
  const { fetchTaxes } = useTaxService();

  const [contact_brackets, setContactBrackets] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [is_fetching_modules, setFetchingModules] = useState(true);
  const [is_purchasing, setPurchasing] = useState(false);
  const [modules, setModules] = useState([]);
  const [payment_details, setPaymentDetails] = useState({});
  const [selected_bracket, setSelectedBracket] = useState({ min: 0, max: 999 });
  const [selected_currency, setSelectedCurrency] = useState({});
  const [selected_module, setSelectedModule] = useState({});
  const [selected_renewal_period, setSelectedRenewalPeriod] = useState('month');
  const [show_contact_sales_modal, setShowContactSalesModal] = useState(false);
  const [tax_rates, setTaxRates] = useState({});
  const [tenant_class, setTenantClass] = useState('old');

  useEffect(() => {
    if (!module_code) return;
    fetchTaxes().then(({ taxes }) => {
      setTaxRates(() =>
        taxes.reduce(
          (s, rate) => ({
            ...s,
            [rate.currency]: rate.tax
          }),
          {}
        )
      );
    });

    fetchCurrencies().then(({ currencies }) => {
      const parsed_currencies = currencies.map((currency) => ({
        ...currency,
        value: currency.code,
        label: currency.code
      }));
      setCurrencies(parsed_currencies);
      setSelectedCurrency(
        parsed_currencies.find((currency) => currency.code === 'NGN') || parsed_currencies[0]
      );
    });

    setFetchingModules(true);
    Promise.all([
      fetchContacts({ query_string: 'count=1' }),
      fetchModules({ query_string: `code=${module_code}` }),
      fetchSubscription({ query_string: `price.amount=1~&plan_code=${module_code}&count=1` })
    ])
      .then(([{ size: contact_count }, { modules }, { size: subscription_count }]) => {
        setModules(modules.sort((a, b) => a.tier_index - b.tier_index));
        const brackets_map = {};
        for (const module of modules) {
          for (const pricing of module.pricing) {
            const { contact_bracket } = pricing;
            const { min, max } = contact_bracket;
            const bracket_key = `${min}-${max}`;
            if (!brackets_map[bracket_key]) {
              brackets_map[bracket_key] = {
                min,
                max,
                label: `${toReadableNumber(min)} - ${toReadableNumber(max)}`,
                value: `${min}:${max}`
              };
            }
          }
        }

        const all_values = Object.values(brackets_map);
        const valid_brackets = all_values.filter(
          (bracket) => contact_count <= bracket.min || contact_count <= bracket.max
        );

        const sorted_brackets = valid_brackets.sort((a, b) => Number(a.min) - Number(b.min));
        const last_bracket = sorted_brackets[sorted_brackets.length - 1];
        const contact_sales = {
          min: Number(last_bracket.max) + 1,
          max: Number(last_bracket.max) + 100000,
          label: `More than ${toReadableNumber(last_bracket.max)}`,
          value: `sales`
        };
        setContactBrackets([...sorted_brackets, contact_sales]);
        setSelectedBracket(sorted_brackets[0]);
        setSelectedModule(modules[1]);
        setTenantClass(subscription_count > 0 ? 'old' : 'new');
      })
      .finally(() => setFetchingModules(false));
  }, [module_code]);

  const handleClose = () => {
    setModuleCode('');
    closeModal();
  };

  const handleDisable = (tier) => {
    if (selected_module.tier === tier) {
      setSelectedModule(modules.find((module) => module.tier === selected_module.upgrades_to));
    }
  };

  const handleFreemiumPurchase = async () => {
    const data = {
      module: { ...selected_module, pricing: payment_details.pricing },
      currency: selected_currency,
      renewal_period: selected_renewal_period
    };

    setPurchasing(true);
    const { subscription } = await buyFreemiumSubscription({ data });
    if (subscription) {
      window.location.href = '/dashboard';
      return;
    }
    setPurchasing(false);
  };

  const handlePaidPurchase = async () => {
    if (!tenant || !tenant.id) return toast.error('Unable to process tenant data');

    const { amount_to_pay, currency } = payment_details;
    const config = prepareCheckoutConfig({
      tenant,
      amount: amount_to_pay,
      currency,
      tx_reason: 'subscription',
      payment_options: 'card'
    });

    const data = {
      currency: currency.code,
      conversion_rate: currency.exchange_rate,
      amount: amount_to_pay,
      tenant_id: tenant.id,
      tx_ref: config.tx_ref,
      reason: 'subscription',
      metadata: {
        module: { ...selected_module, pricing: payment_details.pricing },
        ...payment_details
      }
    };

    setPurchasing(true);
    const payment_record = await createPayment({ data });
    if (payment_record) {
      setVirtualPaymentDetails({
        amount: amount_to_pay,
        flw_config: config,
        currency: currency.code,
        payment_id: payment_record.id,
        plan_name: selected_module.title,
        tenant,
        tx_ref: config.tx_ref
      });
      openModal();
    }
    setPurchasing(false);
  };

  const handlePayment = async () => {
    const { amount_to_pay } = payment_details;
    if (amount_to_pay === 0) return await handleFreemiumPurchase();
    else return await handlePaidPurchase();
  };

  const is_contact_sales = selected_bracket.value === 'sales';

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span />
        <span className={styles.closeButton}>
          <Icon name="close" onClick={handleClose} />
        </span>
      </div>
      <div className={styles.body}>
        <div className={styles.information}>
          <h1>Choose the plan that turns your customers into revenue.</h1>
          <div className={styles.renewalPeriod}>
            <span
              className={cx({ [styles.selected]: selected_renewal_period === 'month' })}
              onClick={() => setSelectedRenewalPeriod('month')}
            >
              Monthly billing
            </span>
            <span
              className={cx({ [styles.selected]: selected_renewal_period === 'year' })}
              onClick={() => setSelectedRenewalPeriod('year')}
            >
              Annual billing
            </span>
          </div>
          <div className={styles.tuners}>
            <div className={styles.currencyTuner}>
              <MultiSelect
                options={currencies}
                onChange={setSelectedCurrency}
                value={selected_currency}
              />
            </div>
            <div className={styles.contactTuner}>
              <span>How many subscribers do you have?</span>
              <div className={styles.contactSelect}>
                <MultiSelect
                  options={contact_brackets}
                  value={selected_bracket}
                  onChange={setSelectedBracket}
                  isSorted={false}
                />
              </div>
            </div>
          </div>
        </div>
        <Spacer multiple={4} />
        {is_fetching_modules ? (
          <div className={styles.spinnerWrapper}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.modules}>
            {modules.map((module) => (
              <SubscriptionPurchaseItem
                key={module.id}
                contact_bracket={selected_bracket}
                currency={selected_currency}
                is_selected={selected_module?.tier === module.tier}
                module={module}
                onDisable={handleDisable}
                onSelect={setSelectedModule}
                onPricingChange={setPaymentDetails}
                renewal_period={selected_renewal_period}
                tax_rates={tax_rates}
                tenant_class={tenant_class}
              />
            ))}
          </div>
        )}
        {!isMobile && (
          <>
            <Spacer multiple={20} />
            <div className={styles.pricingComparison}>
              <PricingComparison
                contact_bracket={selected_bracket}
                currency={selected_currency?.code}
                renewal_period={selected_renewal_period?.value}
                modules={modules}
                onGetStarted={setSelectedModule}
                onBookCall={() => setShowContactSalesModal(true)}
              />
            </div>
          </>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.left}>
          <div className={styles.summaryItem}>
            <span className={styles.itemTitle}>Name</span>
            <span className={styles.itemValue}>{selected_module.title} Plan</span>
          </div>
        </div>
        <div className={styles.right}>
          {!isMobile && (
            <>
              <div className={styles.summaryItem}>
                <span className={styles.itemTitle}>Max. contacts</span>
                {is_contact_sales ? (
                  <span> - </span>
                ) : (
                  <span className={styles.itemValue}>
                    {toReadableNumber(selected_bracket?.max)}
                  </span>
                )}
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.itemTitle}>Cost</span>
                {is_contact_sales ? (
                  <span> - </span>
                ) : (
                  <span className={styles.itemValue}>
                    {getCurrencySymbol(selected_currency?.code)}
                    {toReadableNumber(payment_details.discounted_price)}
                  </span>
                )}
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.itemTitle}>Tax({payment_details.tax_rate}%)</span>
                {is_contact_sales ? (
                  <span> - </span>
                ) : (
                  <span className={styles.itemValue}>
                    {getCurrencySymbol(selected_currency?.code)}
                    {toReadableNumber(payment_details.tax)}
                  </span>
                )}
              </div>
            </>
          )}
          <div className={styles.summaryItem}>
            <span className={styles.itemTitle}>
              Billed {selected_renewal_period === 'month' ? 'monthly' : 'annually'}
            </span>
            {is_contact_sales ? (
              <span> - </span>
            ) : (
              <span className={styles.itemValue}>
                {getCurrencySymbol(selected_currency?.code)}
                {toReadableNumber(payment_details.amount_to_pay)}
              </span>
            )}
          </div>
          {is_contact_sales ? (
            <Button text="Talk to sales" onClick={() => setShowContactSalesModal(true)} />
          ) : (
            <Button
              text="Pay"
              className={styles.payBtn}
              loading={is_purchasing}
              disabled={is_purchasing}
              onClick={handlePayment}
            />
          )}
        </div>
      </div>
      <GmModal
        title="Schedule a Call with Our Team"
        show_modal={show_contact_sales_modal}
        show_title
        onClose={() => setShowContactSalesModal(false)}
      >
        <ContactSales />
      </GmModal>
    </div>
  );
};
